import React, { useState, useEffect } from "react";
import submit from "submit-form";
import moment from "moment";
import AdminToggle from "./components/AdminToggle";
import { useRillContext } from "../../rill/RillReact/share/base";
import ConfirmationModal from "../../parts/modals/ConfirmationModal";
import Pagination from "../../parts/pagination";
import FilterChips from "../athlete-portal/calendar/FilterChips";

const NewsView = () => {
  const { locals } = useRillContext();
  const { adminNews = {}, query = {} } = locals;
  const docs = adminNews.docs || [];
  const total = adminNews.total || 0;
  const activePage = parseInt(query.page, 10) || 1;

  const [busyIds, setBusyIds] = useState({});
  const [idToDelete, setIdToDelete] = useState(undefined);

  const filterChips = [];
  if (query.filterFrom || query.filterTo) {
    const fromLabel = query.filterFrom
      ? moment(query.filterFrom).format("MMM D, YYYY")
      : "...";
    const toLabel = query.filterTo
      ? moment(query.filterTo).format("MMM D, YYYY")
      : "...";
    filterChips.push({ id: "dateRange", text: `${fromLabel} — ${toLabel}` });
  }
  if (query.filterPublished === 1) {
    filterChips.push({ id: "published", text: "Published" });
  }else if (query.filterPublished === 0){
    filterChips.push({ id: "unpublished", text: "Unpublished" });
  }

  const openFilterModal = () => {
    submit("/actions/refresh/?", {
      method: "GET",
      body: { "modal[type]": "filter-news" },
    });
  };

  const clearFilter = (id) => {
    const body = {};
    if (id === "dateRange") {
      body.filterFrom = "";
      body.filterTo = "";
    } else if (id === "published") {
      body.filterPublished = "";
    }else if (id === "unpublished"){
      body.filterPublished = ""
    }
    submit("/actions/refresh/?", { method: "GET", body });
  };

  const clearAllFilters = () => {
    submit("/actions/refresh/?", {
      method: "GET",
      body: { filterFrom: "", filterTo: "", filterPublished: "" },
    });
  };

  useEffect(() => {
    setBusyIds({});
  }, [docs]);

  const togglePublished = (item) => {
    setBusyIds((prev) => ({ ...prev, [item._id]: true }));
    submit(`/actions/news/update`, {
      method: "POST",
      body: { newsId: item._id, published: !item.published },
    });
  };

  const openCreate = () => {
    submit("/actions/refresh", {
      method: "GET",
      body: { "modal[type]": "new-news" },
    });
  };

  const openEdit = (id) => {
    submit("/actions/refresh", {
      method: "GET",
      body: { "modal[type]": "edit-news", newsId: id },
    });
  };

  const deleteItem = (id) => {
    setIdToDelete(id);
  };

  const handleConfirmDelete = () => {
    const body = { newsId: idToDelete };
    if (docs.length === 1 && activePage > 1) {
      body.page = activePage - 1;
    }
    submit(`/actions/news/delete`, { method: "POST", body });
    setIdToDelete(undefined);
  };

  const handleCancelDelete = () => {
    setIdToDelete(undefined);
  };

  const getTextExcerpt = (text) => {
    if (!text) return "";
    try {
      const doc = typeof text === "string" ? JSON.parse(text) : text;
      const collect = (node) => {
        if (!node) return "";
        if (node.type === "text") return node.text || "";
        if (Array.isArray(node.content)) return node.content.map(collect).join("");
        return "";
      };
      return collect(doc).slice(0, 120);
    } catch {
      return String(text).slice(0, 120);
    }
  };

  return (
    <main className="admin">
      <AdminToggle />

      <div className="admin-news-header">
        <h2>News</h2>
        <div className="admin-news-header-actions">
          {filterChips.length > 0 && (
            <FilterChips
              filters={filterChips}
              clearAllFilters={clearAllFilters}
              clearFilter={clearFilter}
            />
          )}
          <button
            data-button-type="secondary"
            type="button"
            onClick={openFilterModal}
          >
            Filter
          </button>
          <button data-button-type="primary" onClick={openCreate} type="button">
            Add News
          </button>
        </div>
      </div>

      <section role="table-wrapper" data-borders-inside>
        <table role="report-table">
          <thead role="report-header">
            <tr>
              <th>Title</th>
              <th style={{ width: "35%" }}>Text</th>
              <th>Video/Photo</th>
              <th>Chips</th>
              <th style={{ width: "8.5rem", whiteSpace: "nowrap" }}>Date</th>
              <th>Publish</th>
              <th style={{ width: "7rem" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.length === 0 && (
              <tr>
                <td colSpan={7}>No announcements yet.</td>
              </tr>
            )}
            {docs.map((item) => (
              <tr key={item._id}>
                <td>{item.title}</td>
                <td className="admin-news-excerpt">{getTextExcerpt(item.text)}</td>
                <td>
                  {item.imageUrl ? (
                    <img
                      src={item.imageUrl}
                      alt=""
                      className="admin-news-thumbnail"
                    />
                  ) : (
                    "—"
                  )}
                </td>
                <td>
                  {(item.chips || []).map((chip, i) => (
                    <span
                      key={i}
                      className="chip"
                      style={{
                        backgroundColor: `${chip.color}20`,
                        color: chip.color,
                      }}
                    >
                      {chip.text}
                    </span>
                  ))}
                </td>
                <td>{item.createdAt ? moment(item.createdAt).format("MMMM D, YYYY") : "—"}</td>
                <td>
                  <input
                    type="checkbox"
                    role="switch"
                    checked={!!item.published}
                    disabled={!!busyIds[item._id]}
                    onChange={() => togglePublished(item)}
                  />
                </td>
                <td className="actions" align="center" valign="middle">
                  <div className="admin-news-actions">
                    <button
                      type="button"
                      data-button-type="secondary"
                      onClick={() => openEdit(item._id)}
                      className="m-0!"
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      data-button-type="danger"
                      data-action="cancel"
                      data-icon
                      onClick={() => deleteItem(item._id)}
                      className="m-0!"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <Pagination total={total} pageSize={12} />
      <ConfirmationModal
        isOpen={idToDelete !== undefined}
        title="Delete announcement?"
        contentText={`Are you sure you want to delete this announcement? \n If you delete it, you won't be able to recover it.`}
        cancelButtonText="Cancel"
        cancelButtonAction={handleCancelDelete}
        confirmButtonText="Delete"
        confirmButtonAction={handleConfirmDelete}
        icon="delete"
      />
    </main>
  );
};

export default NewsView;
